/**
 * KnowledgeCard — 知识库卡片组件
 *
 * 展示知识文档：标题 + 类型Tag(彩色) + 国家Tag(灰色) + 简介 + 时间
 * 支持 framer-motion hover 微动画，编辑/删除操作按钮
 */

import { Card, Tag, Typography, Space, Modal, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, GlobalOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import { KNOWLEDGE_TYPES, COUNTRIES } from '../api/knowledge';

const { Text, Title } = Typography;

/**
 * @param {Object} props
 * @param {Object} props.document - 知识文档对象
 * @param {Function} [props.onEdit] - 编辑回调
 * @param {Function} [props.onDelete] - 删除回调
 */
export default function KnowledgeCard({ document, onEdit, onDelete }) {
  const { id, title, type, country, summary, updatedAt, agentRefs } = document;

  const typeConfig = KNOWLEDGE_TYPES[type] || { label: type, color: 'default' };
  const countryConfig = COUNTRIES.find((c) => c.code === country) || { label: country };

  const handleDelete = () => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除知识文档《${title}》吗？删除后不可恢复。`,
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      centered: true,
      onOk: () => onDelete?.(id),
    });
  };

  const handleEdit = () => {
    onEdit?.(document);
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
    >
      <Card
        style={{
          height: '100%',
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 12,
          display: 'flex',
          flexDirection: 'column',
        }}
        styles={{
          body: { flex: 1, display: 'flex', flexDirection: 'column', padding: '20px' },
        }}
        hoverable
      >
        {/* Title */}
        <Title
          level={5}
          ellipsis={{ tooltip: title }}
          style={{
            color: '#f0f5fa',
            fontSize: 15,
            fontWeight: 600,
            lineHeight: 1.4,
            marginBottom: 10,
          }}
        >
          {title}
        </Title>

        {/* Tags */}
        <Space size={6} style={{ marginBottom: 12 }}>
          <Tag
            color={typeConfig.color}
            style={{
              fontSize: 11,
              fontWeight: 500,
              borderRadius: 6,
              padding: '1px 8px',
              border: 'none',
            }}
          >
            {typeConfig.label}
          </Tag>
          <Tag
            style={{
              fontSize: 11,
              fontWeight: 400,
              borderRadius: 6,
              padding: '1px 8px',
              background: 'rgba(255,255,255,0.08)',
              color: 'rgba(255,255,255,0.55)',
              border: 'none',
            }}
          >
            <GlobalOutlined style={{ marginRight: 3, fontSize: 10 }} />
            {countryConfig.label}
          </Tag>
        </Space>

        {/* Summary */}
        <Text
          type="secondary"
          style={{
            flex: 1,
            fontSize: 12,
            lineHeight: 1.6,
            color: 'rgba(255,255,255,0.45)',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            marginBottom: 16,
          }}
        >
          {summary}
        </Text>

        {/* Footer: date + actions */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            paddingTop: 12,
            marginTop: 'auto',
          }}
        >
          <Space size={8}>
            <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
              {updatedAt}
            </Text>
            <Text style={{ fontSize: 11, color: 'rgba(232,210,138,0.6)' }}>
              引用 {agentRefs}
            </Text>
          </Space>

          <Space size={4}>
            <Tooltip title="编辑">
              <button
                onClick={handleEdit}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 6,
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'rgba(255,255,255,0.45)',
                  transition: 'all 150ms ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(99,102,241,0.4)';
                  e.currentTarget.style.color = '#6366f1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                  e.currentTarget.style.color = 'rgba(255,255,255,0.45)';
                }}
              >
                <EditOutlined style={{ fontSize: 12 }} />
              </button>
            </Tooltip>
            <Tooltip title="删除">
              <button
                onClick={handleDelete}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 6,
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'rgba(255,255,255,0.45)',
                  transition: 'all 150ms ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(248,113,113,0.4)';
                  e.currentTarget.style.color = '#f87171';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                  e.currentTarget.style.color = 'rgba(255,255,255,0.45)';
                }}
              >
                <DeleteOutlined style={{ fontSize: 12 }} />
              </button>
            </Tooltip>
          </Space>
        </div>
      </Card>
    </motion.div>
  );
}
